import { ARIA, CLASS_NAMES, EVENTS, MISC, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Floating Nav block.
 *
 * On mobile  : renders as a dropdown — tapping the toggle reveals the list of
 *              section links; the active label updates as you scroll.
 * On desktop : renders as a pill nav — the active pill tracks the current
 *              section via IntersectionObserver.
 *
 * The block element renders empty and only paints the background band. The
 * menus live in .floating-nav-sticky, which is hoisted out of the block on
 * init so it becomes the block's next sibling — a sticky element can only
 * travel within its own parent, so it has to sit alongside the section rather
 * than inside it. CSS then pulls it back up over the band with a negative
 * margin, and this class publishes --floating-nav-height so that margin and
 * the band's reserved height stay in step with whatever is rendered.
 *
 * Clicking any link scrolls smoothly to the target section.
 * While any observed section is visible the nav carries the IS_INSIDE_CONTENT
 * class so it can be styled differently when inside the content area.
 */
export default class FloatingNav extends MessageBus {
  /**
   * Constructor function of the FloatingNav class. Must receive one argument,
   * a DOM node representing the floating nav block element.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();

    /**
     * DOM node passed into the constructor
     * @property {HTMLElement} element - The module's container DOM element.
     */
    this.element = element;

    // IntersectionObserver instance — observes each target section for active state
    this.observer = null;

    // IntersectionObserver instance — observes sections to toggle IS_INSIDE_CONTENT
    this.contentObserver = null;

    // ResizeObserver instance — keeps --floating-nav-height in sync
    this.resizeObserver = null;

    // Currently active section id
    this.activeId = null;

    // Initialize the view
    this.init();
  }

  /**
   * Initialises the view by caching DOM references, binding handlers and
   * enabling interactions.
   * @returns {FloatingNav} A reference to the current instance
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find and cache all necessary DOM elements.
   * @returns {FloatingNav} A reference to the current instance
   * @chainable
   */
  cacheDomReferences() {
    // Sticky wrapper holding both menus — hoisted out of the block in enable()
    this.sticky = this.element.querySelector(SELECTORS.FLOATING_NAV_STICKY);

    // Mobile
    this.mobileWrapper = this.element.querySelector(
      SELECTORS.FLOATING_NAV_MOBILE
    );
    this.toggleBtn = this.element.querySelector(SELECTORS.FLOATING_NAV_TOGGLE);
    this.toggleLabel = this.element.querySelector(
      SELECTORS.FLOATING_NAV_TOGGLE_LABEL
    );
    this.dropdown = this.element.querySelector(SELECTORS.FLOATING_NAV_DROPDOWN);
    this.dropdownLinks = Array.from(
      this.element.querySelectorAll(SELECTORS.FLOATING_NAV_DROPDOWN_LINK)
    );

    // Desktop
    this.desktopLinks = Array.from(
      this.element.querySelectorAll(SELECTORS.FLOATING_NAV_LINK)
    );

    // All links combined (for scroll + active logic)
    this.allLinks = [...this.dropdownLinks, ...this.desktopLinks];

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {FloatingNav} A reference to the current instance
   * @chainable
   */
  setupHandlers() {
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleLinkClick = this.handleLinkClick.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleStickyResize = this.handleStickyResize.bind(this);

    return this;
  }

  /**
   * Attach event listeners and start observing sections.
   * @returns {FloatingNav} A reference to the current instance
   * @chainable
   */
  enable() {
    this.hoistSticky();

    if (this.toggleBtn) {
      this.toggleBtn.addEventListener(EVENTS.CLICK, this.handleToggleClick);
    }

    this.allLinks.forEach((link) => {
      link.addEventListener(EVENTS.CLICK, this.handleLinkClick);
    });

    document.addEventListener(EVENTS.CLICK, this.handleOutsideClick);

    this.observeSections();
    this.observeContent();
    this.initActiveState();

    return this;
  }

  /**
   * Moves the sticky wrapper out of the block so it becomes a sibling, then
   * starts tracking its height. Without this the wrapper could only stick
   * within the block's own box, which is the height of the band.
   */
  hoistSticky() {
    if (!this.sticky) return;

    this.element.after(this.sticky);

    this.measureSticky();

    // Re-measure on reflow — breakpoint change, late font load, or pill items
    // wrapping onto a second line
    this.resizeObserver = new ResizeObserver(this.handleStickyResize);
    this.resizeObserver.observe(this.sticky);
  }

  /**
   * ResizeObserver callback — refreshes the published height.
   */
  handleStickyResize() {
    this.measureSticky();
  }

  /**
   * Publishes the rendered menu height so the band can reserve the right
   * amount of space and the negative margin can pull back by the same amount.
   */
  measureSticky() {
    const height = `${this.sticky.offsetHeight}px`;

    this.element.style.setProperty(MISC.FLOATING_NAV_HEIGHT, height);
    this.sticky.style.setProperty(MISC.FLOATING_NAV_HEIGHT, height);
  }

  /**
   * Sets the initial active state on load using the first nav item,
   * so the dropdown hides it immediately without waiting for the observer.
   */
  initActiveState() {
    const firstLink = this.dropdownLinks[0];

    if (!firstLink) return;

    const firstId = firstLink.dataset.navId;

    if (!firstId) return;

    this.activeId = firstId;

    // Apply active class to ALL links (desktop + mobile) for the first item
    this.allLinks.forEach((link) => {
      const isActive = link.dataset.navId === firstId;

      link.classList.toggle(SELECTORS.IS_ACTIVE, isActive);

      // Hide the first item from the dropdown list
      if (this.dropdownLinks.includes(link)) {
        const li = link.closest(SELECTORS.LIST_ITEM);

        li.hidden = isActive;
      }
    });

    if (this.toggleLabel) {
      this.toggleLabel.textContent = firstLink.textContent.trim();
    }
  }

  /**
   * Opens or closes the mobile dropdown.
   */
  handleToggleClick() {
    if (this.mobileWrapper.classList.contains(CLASS_NAMES.IS_OPEN)) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  /**
   * Opens the mobile dropdown with an accessible aria update.
   */
  openDropdown() {
    this.mobileWrapper.classList.add(CLASS_NAMES.IS_OPEN);
    this.toggleBtn.setAttribute(ARIA.EXPANDED, ARIA.TRUE);
    this.dropdown.setAttribute(ARIA.HIDDEN, ARIA.FALSE);
  }

  /**
   * Closes the mobile dropdown with an accessible aria update.
   */
  closeDropdown() {
    this.mobileWrapper.classList.remove(CLASS_NAMES.IS_OPEN);
    this.toggleBtn.setAttribute(ARIA.EXPANDED, ARIA.FALSE);
    this.dropdown.setAttribute(ARIA.HIDDEN, ARIA.TRUE);
  }

  /**
   * Closes the dropdown when the user clicks outside the nav.
   * @param {MouseEvent} e - The click event that occurred outside the nav.
   */
  handleOutsideClick(e) {
    // The sticky wrapper is no longer a descendant of the block, so both have
    // to be checked before treating a click as "outside"
    const isInside =
      this.element.contains(e.target) || this.sticky?.contains(e.target);

    if (!isInside) {
      this.closeDropdown();
    }
  }

  /**
   * Intercepts nav link clicks and performs a smooth scroll to the target
   * section, closing the mobile dropdown if open.
   * @param {MouseEvent} e - The click event from the nav link.
   */
  handleLinkClick(e) {
    const href = e.currentTarget.getAttribute(SELECTORS.HREF);

    if (!href || !href.startsWith(SELECTORS.HASH)) return;

    const target = document.querySelector(href);

    if (!target) return;

    e.preventDefault();

    // Update label and hide the clicked item from the dropdown immediately —
    // don't wait for the IntersectionObserver to fire at the end of the scroll
    if (this.toggleLabel) {
      this.toggleLabel.textContent = e.currentTarget.textContent.trim();
    }

    // Restore the previously active dropdown item, hide the newly clicked one
    this.dropdownLinks.forEach((link) => {
      const li = link.closest(SELECTORS.LIST_ITEM);

      li.hidden = link === e.currentTarget;
    });

    this.closeDropdown();

    // Account for the header and the stuck nav so the heading isn't hidden
    const targetTop =
      target.getBoundingClientRect().top +
      window.scrollY -
      MISC.HEADER_OFFSET -
      this.getStickyHeight();

    window.scrollTo({
      top: targetTop,
      behavior: MISC.SMOOTH
    });
  }

  /**
   * Returns the rendered height of the sticky menu.
   * @returns {number} Height in pixels
   */
  getStickyHeight() {
    return this.sticky ? this.sticky.offsetHeight : 0;
  }

  /**
   * Resolves the unique set of section ids referenced by the nav links.
   * @returns {string[]} Array of section ids
   */
  getSectionIds() {
    return [
      ...new Set(
        this.allLinks.map((link) => link.dataset.navId).filter(Boolean)
      )
    ];
  }

  /**
   * Resolves each nav item's target section and begins observing it.
   */
  observeSections() {
    const ids = this.getSectionIds();

    if (!ids.length) return;

    // rootMargin pushes the trigger point down past the header and the nav
    // itself so a section is only "active" once it clears the stuck bar
    const offset = MISC.HEADER_OFFSET + this.getStickyHeight();

    this.observer = new IntersectionObserver(this.handleIntersection, {
      root: null,
      rootMargin: `-${offset}px 0px -60% 0px`,
      threshold: 0
    });

    ids.forEach((id) => {
      const section = document.getElementById(id);

      if (section) this.observer.observe(section);
    });
  }

  /**
   * Observes all target sections and toggles IS_INSIDE_CONTENT on the sticky
   * menu while any of them are visible in the viewport.
   */
  observeContent() {
    const ids = this.getSectionIds();

    if (!ids.length) return;

    this.contentObserver = new IntersectionObserver(
      (entries) => {
        const anyIntersecting = entries.some((entry) => entry.isIntersecting);

        // Applied to the sticky wrapper now, since that's the part that
        // travels over the content
        this.sticky?.classList.toggle(
          CLASS_NAMES.IS_INSIDE_CONTENT,
          anyIntersecting
        );
      },
      { threshold: 0 }
    );

    ids.forEach((id) => {
      const section = document.getElementById(id);

      if (section) this.contentObserver.observe(section);
    });
  }

  /**
   * IntersectionObserver callback — marks the first intersecting section as
   * active and updates all link states and the mobile toggle label.
   * @param {IntersectionObserverEntry[]} entries - Array of IntersectionObserverEntry objects provided by the observer.
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.setActive(entry.target.id);
      }
    });
  }

  /**
   * Applies the IS_ACTIVE class to all links matching the given id and
   * updates the mobile toggle label to reflect the current section.
   * @param {string} id - the section id that is now active
   */
  setActive(id) {
    if (this.activeId === id) return;

    this.activeId = id;

    this.allLinks.forEach((link) => {
      const isActive = link.dataset.navId === id;

      link.classList.toggle(SELECTORS.IS_ACTIVE, isActive);

      if (this.desktopLinks.includes(link)) {
        link
          .closest(SELECTORS.LIST_ITEM)
          ?.classList.toggle(SELECTORS.IS_ACTIVE, isActive);
      }

      if (this.dropdownLinks.includes(link)) {
        const li = link.closest(SELECTORS.LIST_ITEM);

        li.hidden = isActive;
      }

      if (isActive && this.toggleLabel && this.dropdownLinks.includes(link)) {
        this.toggleLabel.textContent = link.textContent.trim();
      }
    });
  }

  /**
   * Tears down event listeners, observers, and returns the sticky wrapper to
   * its original position inside the block.
   */
  destroy() {
    if (this.toggleBtn) {
      this.toggleBtn.removeEventListener(EVENTS.CLICK, this.handleToggleClick);
    }

    this.allLinks.forEach((link) => {
      link.removeEventListener(EVENTS.CLICK, this.handleLinkClick);
    });

    document.removeEventListener(EVENTS.CLICK, this.handleOutsideClick);

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.contentObserver) {
      this.contentObserver.disconnect();
      this.contentObserver = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.sticky) {
      this.element.appendChild(this.sticky);
      this.sticky.style.removeProperty(MISC.FLOATING_NAV_HEIGHT);
    }

    this.element.style.removeProperty(MISC.FLOATING_NAV_HEIGHT);
  }
}
